import { useQueryClient } from '@tanstack/react-query';
import { DateTime, Info } from 'luxon';
import Papa from 'papaparse';
import { ChangeEvent, DragEvent, FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as XLSX from 'xlsx';

import { ReactComponent as UploadIcon } from '@/assets/icons/upload.svg';
import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import {
  Label,
  RadioCircle,
  RadioInput,
  Radios,
  RadiosLabel,
} from '@/components/Forms';
import { Option, StyledSelect } from '@/components/Forms/Dropdown';
import {
  Dropzone,
  InnerLabel,
  Notice,
  Title,
} from '@/components/Forms/FileField';
import { SrOnly } from '@/components/Layout/Parts';
import { Modal } from '@/components/Modal';
import { Table, Td, Th } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { api } from '@/utils/api';
import { hexToRgba, scrollBarStyles } from '@/utils/theme';

const IMPORT_ENDPOINT = '/v1/payments/invoices/import';

const ACCEPTED_EXTENSIONS = ['csv', 'xls', 'xlsx'];

const SENDER_EMAILS = [
  'partnerships@boredpanda.com',
  'spikeinvoices@boredpanda.com',
];

// Sits in the same radio group as the addresses: importing without notifying
// anyone is a choice of sender, not a separate toggle.
const NO_EMAILS = 'none';

const SENDER_OPTIONS = [
  ...SENDER_EMAILS.map((email) => ({ value: email, label: email })),
  { value: NO_EMAILS, label: 'Do not send the emails' },
];

const DEFAULT_SENDER = SENDER_EMAILS[0];

// The revenue month the imported invoices belong to; the year is the backend's
// to decide, the picker only offers the twelve month names.
const MONTH_OPTIONS: Option<number>[] = Info.months('long').map(
  (name, index) => ({ value: index + 1, label: name }),
);

const Dialog = styled(StyledDialog)`
  width: 1100px;
  max-width: calc(100vw - 80px);
  padding: 40px 32px 24px;
  text-align: left;
  display: flex;
  flex-direction: column;
`;

const Heading = styled(Text)`
  margin-bottom: 24px;
`;

const SenderBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 24px;
`;

const SenderHeading = styled(Text)`
  margin-bottom: 4px;
`;

const SenderRadios = styled(Radios)`
  flex-wrap: wrap;
  gap: 12px 32px;
`;

const MonthField = styled.div`
  width: 240px;
  margin-bottom: 24px;
`;

// Once a file is loaded the preview fills the dialog, so the drop target is the
// dialog itself and this covers it to say so. Click-through, or it would eat
// the dragleave that clears the state.
const DropOverlay = styled.div`
  position: absolute;
  inset: 8px;
  z-index: 2;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${({ theme }) => theme.colors.purpleLight};
  border-radius: 4px;
  background: ${({ theme }) => hexToRgba(theme.colors.white, 0.9)};
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

// The global reset only gives buttons `font-family: inherit`, so a bare button
// lands on the UA font. Spell out the preset that the "browse" link uses.
const TextButton = styled.button`
  font-family: ${({ theme }) => theme.typography.smallBold.fontFamily};
  font-size: ${({ theme }) => theme.typography.smallBold.fontSize};
  font-weight: ${({ theme }) => theme.typography.smallBold.fontWeight};
  line-height: ${({ theme }) => theme.typography.smallBold.lineHeight};
  color: ${({ theme }) => theme.colors.purple};
  text-decoration: underline;
`;

// Same shell as ResponsiveTable, plus the vertical cap the modal needs so the
// preview scrolls instead of pushing the footer off screen.
const TableHolder = styled.div`
  overflow: auto;
  max-height: calc(90vh - 380px);
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  ${scrollBarStyles};
`;

const PreviewTable = styled(Table)`
  /* separate so the sticky header keeps its bottom border while scrolling */
  border-collapse: separate;
  border-spacing: 0;
  th,
  td {
    padding: 12px;
    font-size: 11px;
    line-height: 16px;
    white-space: nowrap;
  }
  th {
    position: sticky;
    top: 0;
    z-index: 1;
    /* column names are shown exactly as the file spells them */
    text-transform: none;
  }
  td {
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* zebra striping to keep a wide, dense grid readable across columns */
  tbody tr:nth-child(even) td {
    background: ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

const ErrorText = styled(Text)`
  margin-top: 16px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
`;

const getExtension = (name: string) => name.split('.').pop()?.toLowerCase();

const parseCsv = (file: File) =>
  new Promise<string[][]>((resolve, reject) => {
    Papa.parse<string[]>(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => resolve(results.data),
      error: (error: Error) => reject(error),
    });
  });

const parseExcel = async (file: File) => {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];

  if (!sheet) {
    return [];
  }

  return XLSX.utils.sheet_to_json<string[]>(sheet, {
    header: 1,
    blankrows: false,
    defval: '',
    raw: false,
  });
};

interface Props {
  onClose: VoidFunction;
}

const InvoicesImportModal: FC<Props> = ({ onClose }) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();

  const inputRef = useRef<HTMLInputElement>(null);

  // dragenter/dragleave also fire when crossing child elements, so the depth is
  // counted rather than toggled — otherwise the overlay flickers.
  const dragDepth = useRef(0);

  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [sender, setSender] = useState(DEFAULT_SENDER);
  const [month, setMonth] = useState<number>(DateTime.now().month);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // A file dropped next to the dialog rather than on it would otherwise be
  // opened by the browser, navigating away from the half-finished import.
  useEffect(() => {
    const swallow = (e: globalThis.DragEvent) => e.preventDefault();

    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);

    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  const reset = () => {
    setFile(null);
    setHeaders([]);
    setRows([]);
    setError('');
  };

  const handleFile = async (selected: File) => {
    reset();

    const extension = getExtension(selected.name);

    if (!extension || !ACCEPTED_EXTENSIONS.includes(extension)) {
      setError('Unsupported file format. Upload a CSV or Excel file.');
      return;
    }

    setIsParsing(true);

    try {
      const data =
        extension === 'csv'
          ? await parseCsv(selected)
          : await parseExcel(selected);

      const [headerRow, ...dataRows] = data.filter((row) =>
        row.some((cell) => `${cell ?? ''}`.trim() !== ''),
      );

      if (!headerRow?.length) {
        setError('The file is empty.');
        return;
      }

      if (!dataRows.length) {
        setError('The file has no data rows.');
        return;
      }

      setFile(selected);
      setHeaders(headerRow.map((header) => `${header ?? ''}`));
      setRows(
        dataRows.map((row) => headerRow.map((_, i) => `${row[i] ?? ''}`)),
      );
    } catch (err) {
      setError(
        'Could not read the file. Make sure it is a valid CSV or Excel file.',
      );
    } finally {
      setIsParsing(false);
    }
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];

    // allow picking the same file again after a reset
    e.target.value = '';

    if (selected) {
      handleFile(selected);
    }
  };

  const onDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (e.dataTransfer.types.includes('Files')) {
      dragDepth.current += 1;
      setIsDragging(true);
    }
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    // without this the browser opens the file instead of handing it over
    e.preventDefault();
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = Math.max(dragDepth.current - 1, 0);

    if (!dragDepth.current) {
      setIsDragging(false);
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);

    // one invoice file at a time; extra files are ignored rather than merged
    const [dropped] = Array.from(e.dataTransfer.files);

    if (dropped) {
      handleFile(dropped);
    }
  };

  const onConfirm = async () => {
    if (!file || !sender) {
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('revenue_month', String(month));
      // sender_email is left off entirely when nothing is being sent, so the
      // backend never has to read it against a false send_emails.
      formData.append('send_emails', sender === NO_EMAILS ? '0' : '1');

      if (sender !== NO_EMAILS) {
        formData.append('sender_email', sender);
      }

      await api.post(IMPORT_ENDPOINT, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      await queryClient.invalidateQueries(['invoices_list'], { exact: false });

      onClose();

      toasts.create('success', `${rows.length} rows imported`);
    } catch (err) {
      alerts.error('Import failed', 'The file could not be imported.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <Heading $type="heading2">Import invoices</Heading>

        <MonthField>
          <Label htmlFor="import-month">Revenue month</Label>
          <StyledSelect
            inputId="import-month"
            classNamePrefix="react-select"
            options={MONTH_OPTIONS}
            value={MONTH_OPTIONS.find((option) => option.value === month)}
            onChange={(option) => setMonth((option as Option<number>).value)}
            isSearchable={false}
          />
        </MonthField>

        <SenderBox>
          <SenderHeading $type="smallBold">Send invoices from</SenderHeading>
          <Text $type="smallNormal" $color="gray">
            Pick the mailbox the imported invoices will be sent from, or import
            them without notifying anyone
          </Text>
          <SenderRadios>
            {SENDER_OPTIONS.map((option) => (
              <RadiosLabel key={option.value}>
                {option.label}
                <RadioInput
                  type="radio"
                  name="sender_email"
                  checked={sender === option.value}
                  onChange={() => setSender(option.value)}
                />
                <RadioCircle />
              </RadiosLabel>
            ))}
          </SenderRadios>
        </SenderBox>

        <SrOnly
          as="input"
          ref={inputRef}
          type="file"
          id="import-file"
          accept=".csv,.xls,.xlsx"
          onChange={onFileChange}
        />

        {!rows.length ? (
          <Dropzone $dragging={isDragging}>
            <UploadIcon />
            <Title $type="smallBold" $color="gray" as="div">
              {isParsing ? (
                'Reading file…'
              ) : (
                <>
                  Drop file here or{' '}
                  <InnerLabel
                    as="label"
                    $type="smallBold"
                    $color="purple"
                    htmlFor="import-file"
                  >
                    browse
                  </InnerLabel>
                </>
              )}
            </Title>
            <Notice $type="extraSmall" $color="gray" as="div">
              Supports: CSV, XLS, XLSX
            </Notice>
          </Dropzone>
        ) : (
          <>
            <FileRow>
              <Text $type="caption">
                <strong>{file?.name}</strong> — {rows.length}{' '}
                {rows.length === 1 ? 'row' : 'rows'}
              </Text>
              <TextButton
                type="button"
                onClick={() => inputRef.current?.click()}
              >
                Choose another file
              </TextButton>
            </FileRow>
            <TableHolder>
              <PreviewTable>
                <thead>
                  <tr>
                    <Th>#</Th>
                    {headers.map((header, index) => (
                      <Th key={`${header}-${index}`}>{header}</Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <Td $color="gray">{rowIndex + 1}</Td>
                      {row.map((cell, cellIndex) => (
                        <Td key={cellIndex} title={cell}>
                          {cell}
                        </Td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </PreviewTable>
            </TableHolder>
          </>
        )}

        {isDragging && !!rows.length && (
          <DropOverlay>
            <Text $type="smallBold" $color="gray">
              Drop the file to replace the current one
            </Text>
          </DropOverlay>
        )}

        {!!error && (
          <ErrorText $type="caption" $color="error">
            {error}
          </ErrorText>
        )}

        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>

        <Footer>
          <Button type="button" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            color="primary"
            disabled={!rows.length || !sender || isSubmitting}
            loading={isSubmitting}
            onClick={onConfirm}
          >
            Confirm
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default InvoicesImportModal;
